import {
  Component, OnInit, forwardRef, AfterViewInit, OnDestroy,
  ViewChild, ElementRef, Output, EventEmitter, Input, Renderer2, OnChanges, SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  NG_VALUE_ACCESSOR, ControlValueAccessor, Validator,
  AbstractControl, ValidationErrors, NG_VALIDATORS
} from '@angular/forms';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

@Component({
  selector: 'intl-tel-input',
  templateUrl: './intl-tel-input.component.html',
  styleUrls: ['./intl-tel-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IntlTelInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => IntlTelInputComponent),
      multi: true
    }
  ],
  standalone: true,
  imports: [CommonModule]
})
export class IntlTelInputComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor, Validator {
  @Input() cssClass = 'form-control';
  @Input() preferredCountries: string[] = ['in', 'us', 'gb'];
  @Input() onlyCountries: string[] = [];
  @Input() initialCountry = 'in';
  @Input() enablePlaceholder = true;
  @Input() searchCountryFlag = true;
  @Input() searchCountryPlaceholder = 'Search Country';
  @Input() separateDialCode = true;

  @ViewChild('phoneInput', { static: true }) phoneInput: ElementRef;

  @Output() countryChange = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<boolean>();

  private iti: any;
  private _value = '';
  private _onChange: (value: any) => void = () => { };
  private _onTouched: () => void = () => { };

  constructor(private renderer: Renderer2) { }

  ngOnInit() { }

  ngAfterViewInit() {
    this.initIti();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.iti && changes['initialCountry'] && changes['initialCountry'].currentValue) {
      this.iti.setCountry(changes['initialCountry'].currentValue);
    }
  }

  private initIti() {
    if (this.iti) {
      this.iti.destroy();
    }

    const options: any = {
      initialCountry: this.initialCountry || 'in',
      preferredCountries: this.preferredCountries,
      onlyCountries: this.onlyCountries,
      separateDialCode: this.separateDialCode,
      autoPlaceholder: this.enablePlaceholder ? 'polite' : 'off',
      utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.15/js/utils.js'
    };

    this.iti = (window as any).intlTelInput(this.phoneInput.nativeElement, options);

    this.renderer.listen(this.phoneInput.nativeElement, 'countrychange', () => {
      const countryData = this.iti.getSelectedCountryData();
      this.countryChange.emit(countryData);
      this.updateValue();
    });

    this.renderer.listen(this.phoneInput.nativeElement, 'input', () => {
      const inputElement = this.phoneInput.nativeElement;
      const currentValue = inputElement.value;
      // Sanitize input to allow only digits, space, +, -, (, )
      const sanitizedValue = currentValue.replace(/[^\d\s\-\+\(\)]/g, '');
      if (currentValue !== sanitizedValue) {
        inputElement.value = sanitizedValue;
      }
      this.updateValue();
    });

    this.renderer.listen(this.phoneInput.nativeElement, 'keypress', (event: KeyboardEvent) => {
      const charCode = event.which ? event.which : event.keyCode;
      // Allow digits (48-57), plus (43), minus (45), space (32), open bracket (40), close bracket (41)
      if (charCode !== 43 && charCode !== 45 && charCode !== 32 && charCode !== 40 && charCode !== 41 && (charCode < 48 || charCode > 57)) {
        event.preventDefault();
      }
    });

    this.renderer.listen(this.phoneInput.nativeElement, 'blur', () => {
      this._onTouched();
    });
  }

  private updateValue() {
    const fullNumber = this.iti.getNumber();
    this._value = fullNumber;
    this._onChange(fullNumber);
    this.statusChange.emit(this.isValid());
  }

  writeValue(value: any): void {
    this._value = value || '';
    if (this.iti) {
      this.iti.setNumber(this._value);
    } else {
      // If iti is not yet initialized, it will be set in ngAfterViewInit
      setTimeout(() => {
        if (this.iti) {
          this.iti.setNumber(this._value);
        }
      }, 0);
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.phoneInput.nativeElement, 'disabled', isDisabled);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    if (this.isValid()) {
      return null;
    }

    return { invalidContactNumber: true };
  }

  private isValid(): boolean {
    if (!this.iti) return false;
    const number = this.iti.getNumber();
    const countryData = this.iti.getSelectedCountryData();

    if (!number) return false;

    try {
      const phoneNumber = parsePhoneNumberFromString(number, countryData.iso2.toUpperCase() as any);
      return phoneNumber ? phoneNumber.isValid() : false;
    } catch (e) {
      return false;
    }
  }

  ngOnDestroy() {
    if (this.iti) {
      this.iti.destroy();
    }
  }
}

